/**
 * Live tuning overlay, toggled with `~`.
 *
 * Every constant in the sim gets a slider that mutates the live constants
 * object in place, so changes land on the very next tick with no restart. The
 * copy/paste JSON round trip exists so a good-feeling set of numbers can be
 * captured and restored in seconds -- this is the part of the project that
 * needs human judgement, so it has to be fast.
 */
import {
  type Constants,
  DEFAULTS,
  TUNING,
  type TuningSpec,
  constantsFromJSON,
} from '@office/sim';

const STYLE = `
.tuning {
  position: fixed;
  top: 0; right: 0; bottom: 0;
  width: 320px;
  padding: 14px 16px 20px;
  overflow-y: auto;
  background: rgba(12, 16, 26, 0.94);
  backdrop-filter: blur(8px);
  border-left: 1px solid rgba(255,255,255,0.12);
  color: #e6ebf5;
  font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace;
  z-index: 50;
}
.tuning[hidden] { display: none !important; }
.tuning h2 {
  margin: 0 0 4px;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9fb3d1;
}
.tuning .hint { margin: 0 0 12px; color: #6f83a3; }
.tuning fieldset {
  border: 0; padding: 0; margin: 0 0 14px;
}
.tuning legend {
  padding: 0; margin-bottom: 6px;
  font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  color: #7f95b8;
}
.tuning .row { margin-bottom: 7px; }
.tuning .row label {
  display: flex; justify-content: space-between; gap: 8px; margin-bottom: 2px;
}
.tuning .row .val { color: #ffd25f; font-variant-numeric: tabular-nums; }
.tuning input[type=range] {
  width: 100%; height: 16px; accent-color: #4fd1c5; cursor: pointer;
}
.tuning .actions { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
.tuning button {
  flex: 1 1 auto;
  padding: 6px 8px;
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 5px;
  background: rgba(255,255,255,0.06);
  color: inherit; font: inherit; cursor: pointer;
}
.tuning button:hover { background: rgba(255,255,255,0.14); }
.tuning textarea {
  width: 100%; height: 84px; resize: vertical;
  border: 1px solid rgba(255,255,255,0.18); border-radius: 5px;
  background: rgba(0,0,0,0.35); color: inherit;
  font: 11px/1.4 ui-monospace, monospace; padding: 6px;
}
.tuning .status { min-height: 14px; color: #4fd1c5; margin-top: 4px; }
`;

export interface TuningPanelOptions {
  /** Called after any change, so the caller can rebuild dependent state. */
  onChange?: (constants: Constants) => void;
  /** Extra buttons, e.g. "reset ball". */
  actions?: { label: string; run: () => void }[];
}

export class TuningPanel {
  readonly element: HTMLElement;
  private readonly rows = new Map<keyof Constants, { input: HTMLInputElement; out: HTMLElement }>();
  private readonly status: HTMLElement;
  private readonly io: HTMLTextAreaElement;
  private statusTimer = 0;

  constructor(
    private readonly constants: Constants,
    private readonly opts: TuningPanelOptions = {},
  ) {
    if (!document.getElementById('tuning-style')) {
      const style = document.createElement('style');
      style.id = 'tuning-style';
      style.textContent = STYLE;
      document.head.append(style);
    }

    this.element = document.createElement('aside');
    this.element.className = 'tuning';
    this.element.hidden = true;

    const title = document.createElement('h2');
    title.textContent = 'Tuning';
    const hint = document.createElement('p');
    hint.className = 'hint';
    hint.textContent = 'Toggle with ~ . Changes apply instantly.';
    this.element.append(title, hint);

    const groups = new Map<string, TuningSpec[]>();
    for (const spec of TUNING) {
      const list = groups.get(spec.group) ?? [];
      list.push(spec);
      groups.set(spec.group, list);
    }
    for (const [group, specs] of groups) {
      this.element.append(this.buildGroup(group, specs));
    }

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.append(
      this.button('Copy JSON', () => void this.copy()),
      this.button('Load JSON', () => this.load()),
      this.button('Defaults', () => this.apply(DEFAULTS, 'Defaults restored')),
    );
    for (const extra of opts.actions ?? []) {
      actions.append(this.button(extra.label, extra.run));
    }

    this.io = document.createElement('textarea');
    this.io.spellcheck = false;
    this.io.placeholder = 'Paste a tuning JSON blob here, then press Load JSON';

    this.status = document.createElement('div');
    this.status.className = 'status';

    this.element.append(actions, this.io, this.status);
    document.body.append(this.element);

    window.addEventListener('keydown', this.onKeyDown);
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.element.remove();
  }

  get visible(): boolean {
    return !this.element.hidden;
  }

  toggle(force?: boolean): void {
    this.element.hidden = force === undefined ? !this.element.hidden : !force;
    if (this.visible) this.refresh();
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Backquote is the `~` key; ignore it while typing in the JSON box.
    if (e.code !== 'Backquote' || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
    e.preventDefault();
    this.toggle();
  };

  private buildGroup(group: string, specs: TuningSpec[]): HTMLElement {
    const fs = document.createElement('fieldset');
    const legend = document.createElement('legend');
    legend.textContent = group;
    fs.append(legend);

    for (const spec of specs) {
      const row = document.createElement('div');
      row.className = 'row';

      const label = document.createElement('label');
      const name = document.createElement('span');
      name.textContent = spec.label;
      const out = document.createElement('span');
      out.className = 'val';
      label.append(name, out);

      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(spec.min);
      input.max = String(spec.max);
      input.step = String(spec.step);
      input.value = String(this.constants[spec.key]);
      out.textContent = format(this.constants[spec.key]);
      input.addEventListener('input', () => {
        const v = Number(input.value);
        if (!Number.isFinite(v)) return;
        this.constants[spec.key] = v;
        out.textContent = format(v);
        this.opts.onChange?.(this.constants);
      });

      label.htmlFor = `tune-${String(spec.key)}`;
      input.id = `tune-${String(spec.key)}`;
      row.append(label, input);
      fs.append(row);
      this.rows.set(spec.key, { input, out });
    }
    return fs;
  }

  private button(label: string, run: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.addEventListener('click', run);
    return b;
  }

  /** Pull slider positions back from the constants object. */
  refresh(): void {
    for (const [key, row] of this.rows) {
      const v = this.constants[key];
      row.input.value = String(v);
      row.out.textContent = format(v);
    }
  }

  private apply(next: Constants, message: string): void {
    for (const key of Object.keys(this.constants) as (keyof Constants)[]) {
      this.constants[key] = next[key];
    }
    this.refresh();
    this.opts.onChange?.(this.constants);
    this.say(message);
  }

  private toJSON(): string {
    return JSON.stringify(this.constants, null, 2);
  }

  private async copy(): Promise<void> {
    const text = this.toJSON();
    this.io.value = text;
    try {
      await navigator.clipboard.writeText(text);
      this.say('Copied to clipboard');
    } catch {
      this.io.select();
      this.say('Clipboard blocked - copy from the box');
    }
  }

  private load(): void {
    const raw = this.io.value.trim();
    if (!raw) {
      this.say('Paste a JSON blob first');
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.say('Not valid JSON');
      return;
    }
    this.apply(constantsFromJSON(parsed, this.constants), 'Loaded');
  }

  private say(message: string): void {
    this.status.textContent = message;
    window.clearTimeout(this.statusTimer);
    this.statusTimer = window.setTimeout(() => {
      this.status.textContent = '';
    }, 2500);
  }
}

function format(v: number): string {
  return Math.abs(v) >= 100 || Number.isInteger(v) ? String(v) : v.toFixed(2);
}
